import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import {
  CheckCircle, WarningCircle, Play, ArrowsClockwise, Clock, Coins,
  Flask, Eye, Buildings, MapPin, CurrencyEur,
} from "@phosphor-icons/react";

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }); } catch { return iso; }
}
const STATUS_STYLE = { finished: "text-accent border-accent/30", running: "text-amber-400 border-amber-500/30", error: "text-destructive border-destructive/30" };
const SRC_LABELS = { "free-work": "Free-Work", "freelance-informatique": "Freelance-Informatique", "codeur": "Codeur" };

export default function ApifyCollection() {
  const [cfg, setCfg] = useState(null);
  const [runs, setRuns] = useState([]);
  const [busy, setBusy] = useState(false);
  const [hoursStr, setHoursStr] = useState("");
  const [maxM, setMaxM] = useState("");
  const [query, setQuery] = useState("");
  const [sinceDays, setSinceDays] = useState("");
  // test mode
  const [tq, setTq] = useState("");
  const [tmax, setTmax] = useState("10");
  const [testing, setTesting] = useState(false);
  const [testRes, setTestRes] = useState(null);

  const load = useCallback(async () => {
    try {
      const [c, r] = await Promise.all([
        api.get("/admin/collection/apify/config"),
        api.get("/admin/collection/apify/runs"),
      ]);
      setCfg(c.data); setRuns(r.data);
      setHoursStr((c.data.hours || []).join(", "));
      setMaxM(String(c.data.max_missions ?? 300));
      setQuery(c.data.query || "");
      setSinceDays(String(c.data.since_days ?? 30));
    } catch { toast.error("Chargement Apify impossible"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveCfg = async (patch) => {
    try {
      const { data } = await api.post("/admin/collection/apify/config", patch);
      setCfg(data); setHoursStr((data.hours || []).join(", "));
      setMaxM(String(data.max_missions ?? 300)); setQuery(data.query || ""); setSinceDays(String(data.since_days ?? 30));
      toast.success("Configuration mise à jour");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const toggleSource = (s) => {
    const cur = cfg.sources || [];
    const next = cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s];
    if (next.length === 0) { toast.error("Gardez au moins une source"); return; }
    saveCfg({ sources: next });
  };

  const collectNow = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/collection/apify/collect", { max_missions: Number(maxM) || undefined });
      if (data.error) toast.error(data.error);
      else toast.success(`Collecte lancée · max ${data.max_missions} missions · ~$${data.estimated_cost}`);
      setTimeout(load, 6000);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const runTest = async () => {
    setTesting(true); setTestRes(null);
    try {
      const { data } = await api.post("/admin/collection/apify/test", { query: tq, max_missions: Number(tmax) || 10 });
      if (data.error) toast.error(data.error); else setTestRes(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setTesting(false); }
  };

  if (!cfg) return <div className="text-muted font-mono text-sm animate-pulse">Chargement Apify…</div>;
  const lastErr = cfg.last_run?.status === "error" ? cfg.last_run?.error : null;

  return (
    <div className="surface rounded-lg p-6 mb-6" data-testid="apify-panel">
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-md bg-accent/12 border border-accent/25 flex items-center justify-center">
            <Buildings size={20} weight="duotone" className="text-accent" />
          </div>
          <div>
            <h3 className="font-display font-bold text-fg flex items-center gap-2">
              Marché IT freelance FR <span className="text-[10px] font-mono text-muted border border-border rounded-full px-2 py-0.5">Apify · Free-Work / Freelance-Info / Codeur</span>
            </h3>
            <p className="text-xs text-muted">Missions IT freelance/ESN avec le VRAI nom d'entreprise, le TJM, le type de contrat et les compétences. Dédupliqué avec les autres sources.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className={`flex items-center gap-1 text-xs font-mono border rounded-full px-2 py-1 ${cfg.configured ? "text-accent border-accent/30" : "text-amber-400 border-amber-500/30"}`} data-testid="apify-status">
            {cfg.configured ? <CheckCircle size={12} weight="fill" /> : <WarningCircle size={12} weight="fill" />} {cfg.configured ? "Configurée" : "Clé manquante"}
          </span>
          <button onClick={() => saveCfg({ enabled: !cfg.enabled })} data-testid="apify-toggle"
            className={`flex items-center gap-1.5 h-8 px-3 rounded-md border text-xs font-medium ${cfg.enabled ? "bg-accent/12 border-accent/40 text-accent" : "bg-surface border-border text-muted hover:text-fg"}`}>
            <CheckCircle size={13} weight={cfg.enabled ? "fill" : "regular"} /> Auto {cfg.enabled ? "activé" : "désactivé"}
          </button>
        </div>
      </div>

      {lastErr && <div className="mb-4 text-xs bg-destructive/10 border border-destructive/30 text-destructive rounded-md px-3 py-2" data-testid="apify-error">{lastErr}</div>}

      {/* Sources + France toggle */}
      <div className="border-t border-border pt-4 mb-3">
        <label className="overline block mb-1.5">Plateformes sources</label>
        <div className="flex flex-wrap gap-2 mb-3">
          {(cfg.all_sources || ["free-work", "freelance-informatique", "codeur"]).map((s) => {
            const on = (cfg.sources || []).includes(s);
            return (
              <button key={s} onClick={() => toggleSource(s)} data-testid={`apify-source-${s}`}
                className={`flex items-center gap-1.5 text-xs rounded-full px-3 py-1 border ${on ? "border-accent/40 text-fg bg-accent/8" : "border-border text-muted bg-surface-2 opacity-60"}`}>
                {on ? <CheckCircle size={13} weight="fill" className="text-accent" /> : <WarningCircle size={13} />} {SRC_LABELS[s] || s}
              </button>
            );
          })}
          <button onClick={() => saveCfg({ france_only: !cfg.france_only })} data-testid="apify-france-toggle"
            className={`flex items-center gap-1.5 text-xs rounded-full px-3 py-1 border ${cfg.france_only ? "border-accent/40 text-fg bg-accent/8" : "border-border text-muted bg-surface-2"}`}>
            <MapPin size={13} weight={cfg.france_only ? "fill" : "regular"} /> France uniquement {cfg.france_only ? "✓" : "✗"}
          </button>
        </div>
      </div>

      <div className="grid sm:grid-cols-4 gap-3 mb-2">
        <div>
          <label className="overline block mb-1.5 flex items-center gap-1"><Clock size={12} /> Heures (UTC)</label>
          <Input className="bg-surface-2 border-border text-xs h-9" value={hoursStr} onChange={(e) => setHoursStr(e.target.value)} onBlur={() => saveCfg({ hours: hoursStr })} placeholder="6, 18" data-testid="apify-hours" />
        </div>
        <div>
          <label className="overline block mb-1.5 flex items-center gap-1"><Coins size={12} /> Max missions / collecte</label>
          <Input type="number" className="bg-surface-2 border-border text-xs h-9" value={maxM} onChange={(e) => setMaxM(e.target.value)} onBlur={() => saveCfg({ max_missions: Number(maxM) })} data-testid="apify-maxm" />
        </div>
        <div>
          <label className="overline block mb-1.5 flex items-center gap-1"><Clock size={12} /> Fraîcheur (jours)</label>
          <Input type="number" className="bg-surface-2 border-border text-xs h-9" value={sinceDays} onChange={(e) => setSinceDays(e.target.value)} onBlur={() => saveCfg({ since_days: Number(sinceDays) })} data-testid="apify-since" />
        </div>
        <div className="flex items-end">
          <button onClick={collectNow} disabled={busy || !cfg.configured} data-testid="apify-collect-now"
            className="w-full flex items-center justify-center gap-2 bg-accent text-black font-semibold px-4 py-2 rounded-md hover-lift disabled:opacity-50 h-9">
            <Play size={15} weight="fill" className={busy ? "animate-pulse" : ""} /> {busy ? "Lancement…" : "Collecter"}
          </button>
        </div>
      </div>
      <div className="mb-3">
        <label className="overline block mb-1.5">Filtre mot-clé (optionnel — vide = tout le marché IT)</label>
        <Input className="bg-surface-2 border-border text-xs h-9" value={query} onChange={(e) => setQuery(e.target.value)} onBlur={() => saveCfg({ query })} placeholder="ex: kubernetes, sap fi (chaque mot doit apparaître)" data-testid="apify-query" />
      </div>
      <p className="text-[11px] text-muted mb-4 flex items-center gap-1.5">
        <Coins size={12} /> ~${cfg.mission_cost_usd}/mission collectée. Le benchmark de TJM est désactivé (aucun coût de benchmark).
      </p>

      {/* Test mode */}
      <div className="border-t border-border pt-4 mb-4">
        <div className="flex items-center gap-2 mb-2"><Flask size={15} className="text-accent" /><h4 className="font-display font-bold text-fg text-sm">Mode test (aucune ingestion)</h4></div>
        <div className="flex flex-wrap gap-2 mb-3">
          <Input className="bg-surface-2 border-border text-xs h-9 w-56" value={tq} onChange={(e) => setTq(e.target.value)} placeholder="Mot-clé (vide = tout)" data-testid="apify-test-query" />
          <Input type="number" className="bg-surface-2 border-border text-xs h-9 w-24" value={tmax} onChange={(e) => setTmax(e.target.value)} placeholder="Max" data-testid="apify-test-max" />
          <button onClick={runTest} disabled={testing || !cfg.configured} className="h-9 px-4 rounded-md bg-surface border border-border text-xs text-fg hover:border-accent flex items-center gap-1 disabled:opacity-50" data-testid="apify-test-run">
            <Eye size={14} /> {testing ? "Test…" : "Tester"}
          </button>
        </div>
        {testRes && (
          <div className="bg-surface-2 border border-border rounded-md p-3 text-xs" data-testid="apify-test-result">
            <div className="font-mono text-muted mb-2">{testRes.raw_count} brut · {testRes.normalized_count} normalisés · {testRes.france_kept} France · {testRes.staffing_excluded} intérim exclus</div>
            <div className="space-y-1.5 max-h-64 overflow-y-auto">
              {(testRes.sample || []).map((n, i) => (
                <div key={i} className="flex items-center gap-2 flex-wrap border-b border-border/50 pb-1">
                  <span className="text-fg font-medium">{n.company_name}</span>
                  <span className="text-muted">— {n.role_title}</span>
                  {n.city && <span className="text-accent font-mono flex items-center gap-0.5"><MapPin size={10} /> {n.city}{n.region ? `, ${n.region}` : ""}</span>}
                  <span className="text-muted font-mono">· {n.posted_at}</span>
                  {(n.salary_min || n.salary_max) && <span className="text-emerald-400 font-mono flex items-center gap-0.5"><CurrencyEur size={10} /> {n.salary_min || "?"}-{n.salary_max || "?"} {n.salary_currency}/{n.salary_period === "day" ? "j" : n.salary_period}</span>}
                  {n.employment_type && <span className="text-muted font-mono">· {n.employment_type}</span>}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Runs */}
      <div className="border-t border-border pt-4">
        <div className="flex items-center justify-between mb-2">
          <h4 className="font-display font-bold text-fg text-sm">Dernières collectes</h4>
          <button onClick={load} className="text-xs text-muted hover:text-accent flex items-center gap-1"><ArrowsClockwise size={12} /> Rafraîchir</button>
        </div>
        <div className="space-y-1.5 max-h-64 overflow-y-auto" data-testid="apify-runs">
          {runs.length === 0 && <span className="text-xs text-muted">Aucune collecte pour l'instant.</span>}
          {runs.map((r) => (
            <div key={r.id} className="text-xs bg-surface-2 border border-border rounded-md px-3 py-2 flex items-center gap-3 flex-wrap">
              <span className={`font-mono border rounded-full px-2 py-0.5 ${STATUS_STYLE[r.status] || "text-muted border-border"}`}>{r.status}</span>
              <span className="text-muted font-mono">{fmtDate(r.created_at)}</span>
              <span className="font-mono text-fg/80">{r.records_received || 0} missions · {r.france_kept || 0} FR</span>
              {r.status === "finished" && <span className="font-mono text-fg/70">{r.created || 0} nouveaux · {r.updated || 0} enrichis · {r.cross_source_merges || 0} cross-source · ${r.estimated_cost || 0}</span>}
              {r.error && <span className="text-destructive">{r.error}</span>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
